import React from 'react'
import clsx from 'clsx'
import { CircularProgress, LinearProgress, Typography } from '@mui/material'
import {
  StatusChipBg,
  StatusChipText,
  StatusChipV4,
} from '@labrav/react-components'
import { useStyles } from './CanvasMigrationPanel.styles'
import { MigrationStatus, MigrationTaskView } from './canvasMigrationTypes'
import { formatRowCount } from './parseCanvasMigrationResponse'

const STATUS_CHIP_PROPS: Record<
  MigrationStatus,
  {
    label: string
    bgColor: StatusChipBg
    textColor: StatusChipText
    borderColor: StatusChipBg
  }
> = {
  pending: {
    label: 'Pending',
    bgColor: StatusChipBg.GRAY,
    textColor: StatusChipText.GRAY,
    borderColor: StatusChipBg.GRAY,
  },
  in_progress: {
    label: 'Running',
    bgColor: StatusChipBg.LIGHTBLUE,
    textColor: StatusChipText.LIGHTBLUE,
    borderColor: StatusChipBg.LIGHTBLUE,
  },
  success: {
    label: 'Completed',
    bgColor: StatusChipBg.GREEN,
    textColor: StatusChipText.GREEN,
    borderColor: StatusChipBg.GREEN,
  },
  failed: {
    label: 'Errors',
    bgColor: StatusChipBg.RED,
    textColor: StatusChipText.RED,
    borderColor: StatusChipBg.RED,
  },
}

/**
 * Read-only progress card for a single migration task. Actions such as retry
 * live in the console header, never inside a card.
 */
export const CanvasMigrationTaskCard: React.FC<{ task: MigrationTaskView }> = ({
  task,
}) => {
  const { classes } = useStyles()
  const chip = STATUS_CHIP_PROPS[task.status]
  const isRunning = task.status === 'in_progress'
  const rowCount = formatRowCount(task.processedRows, task.totalRows)

  return (
    <div
      className={classes.card}
      data-testid={`canvas-migration-task-${task.taskId}`}
    >
      <div className={classes.cardHeader}>
        <Typography className={classes.cardTitle}>{task.name}</Typography>
        <StatusChipV4
          variant="default"
          label={chip.label}
          bgColor={chip.bgColor}
          textColor={chip.textColor}
          borderColor={chip.borderColor}
        />
      </div>

      {task.phaseLabel ? (
        <div
          className={classes.phaseRow}
          data-testid={`canvas-migration-phase-${task.taskId}`}
        >
          {isRunning ? (
            <CircularProgress
              size={16}
              thickness={5}
              className={classes.phaseSpinner}
              aria-label={`${task.name} phase in progress`}
            />
          ) : null}
          <Typography className={classes.phaseLabel}>
            {task.phaseLabel}
          </Typography>
        </div>
      ) : null}

      <div className={classes.metaRow}>
        <Typography className={classes.count}>
          {rowCount
            ? `${task.phasePercentage}% \u00b7 ${rowCount}`
            : `${task.phasePercentage}%`}
        </Typography>
        <Typography className={classes.percent}>{task.progress}%</Typography>
      </div>

      <LinearProgress
        variant="determinate"
        value={task.progress}
        className={clsx(classes.progressBar, {
          [classes.progressBarSucceeded]: task.status === 'success',
          [classes.progressBarFailed]: task.status === 'failed',
        })}
        data-testid={`canvas-migration-progress-${task.taskId}`}
        aria-label={`${task.name} progress`}
      />

      {task.status === 'failed' && task.errorMessage ? (
        <Typography
          className={classes.errorText}
          data-testid={`canvas-migration-error-${task.taskId}`}
        >
          {task.errorMessage}
        </Typography>
      ) : null}
    </div>
  )
}
